import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef } from 'react';
import ReactSelect, { components } from 'react-select';
export { components as selectComp } from 'react-select';
import { theme } from '../../theme.js';
import { Icon } from '../Icon/index.js';
import { useInputGroupContext } from '../InputGroup/index.js';
import { fontStyles } from './styles.js';

/**
 * Style props - docs here: https://react-select.com/styles
 *
 * 1. The component's default style object is passed as the first argument
 * to the function when it's resolved.
 *
 * 2. The second argument is the current state of the select, features like isFocused, isSelected etc.
 * allowing you to implement dynamic styles for each of the components.
 *
 * 3. Use state.selectProps.{your props} to dynamically
 * change styles based on custom props passed
 */
const selectorStyles = {
  option: (styles, _ref) => {
    let {
      isSelected,
      isFocused
    } = _ref;
    return {
      ...styles,
      ...fontStyles,
      color: theme.colors.textPrimary,
      backgroundColor: isSelected ? theme.colors.itemSelected : isFocused ? theme.colors.itemHovered : null,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: theme.colors.itemSelected
      },
      '@media screen and (min-width: 768px)': {
        fontSize: '14px',
        lineHeight: '22px'
      }
    };
  },
  control: (styles, _ref2) => {
    let {
      isFocused,
      isDisabled,
      ...state
    } = _ref2;
    return {
      ...styles,
      ...fontStyles,
      pointerEvents: 'auto',
      cursor: isDisabled ? 'not-allowed' : 'default',
      minHeight: '40px',
      boxShadow: state.selectProps.isInvalid && isFocused ? theme.shadows.error : isFocused ? theme.shadows.focus : 0,
      borderColor: state.selectProps.isInvalid ? theme.colors.inputAlert : isFocused ? theme.colors.inputDefault : isDisabled ? theme.colors.inputDisabled : theme.colors.inputDefault,
      backgroundColor: theme.colors.white,
      color: theme.colors.textPrimary,
      '&:hover': {
        borderColor: state.selectProps.isInvalid ? theme.colors.inputAlert : isFocused ? theme.colors.inputActive : isDisabled ? theme.colors.inputDisabled : theme.colors.inputHover
      },
      '@media screen and (min-width: 768px)': {
        fontSize: '14px',
        lineHeight: '22px'
      }
    };
  },
  placeholder: (styles, _ref3) => {
    let {
      isDisabled
    } = _ref3;
    return {
      ...styles,
      marginLeft: '0px',
      marginRight: '0px',
      color: isDisabled ? theme.colors.inputDisabled : theme.colors.inputPlaceholder
    };
  },
  menu: styles => ({
    ...styles,
    zIndex: theme.zIndices.dropdown
  }),
  menuList: styles => ({
    ...styles,
    paddingBottom: theme.space.space30,
    paddingTop: theme.space.space30,
    // START - Custom visible scroll bar
    // OSx Lion hides scrollbars by default - https://stackoverflow.com/a/10100209/4462743
    // Webkit fix only, no workaround for firefox - design approved
    '::-webkit-scrollbar': {
      WebkitAppearance: 'none',
      width: '7px'
    },
    '::-webkit-scrollbar-thumb': {
      borderRadius: '4px',
      backgroundColor: 'rgba(0, 0, 0, .2)',
      boxShadow: '0 0 1px rgba(255, 255, 255, .5)'
    }
    // END
  }),

  noOptionsMessage: styles => ({
    ...styles,
    color: theme.colors.carbon500,
    '@media screen and (min-width: 768px)': {
      fontSize: '14px',
      lineHeight: '22px'
    }
  }),
  loadingMessage: styles => ({
    ...styles,
    '@media screen and (min-width: 768px)': {
      fontSize: '14px',
      lineHeight: '22px'
    }
  }),
  valueContainer: styles => ({
    ...styles,
    padding: '2px 12px'
  }),
  indicatorSeparator: (styles, _ref4) => {
    let {
      isDisabled
    } = _ref4;
    return {
      ...styles,
      backgroundColor: isDisabled ? theme.colors.inputDisabled : theme.colors.carbon300
    };
  },
  dropdownIndicator: (styles, _ref5) => {
    let {
      isFocused,
      isDisabled
    } = _ref5;
    return {
      ...styles,
      color: isFocused ? theme.colors.carbon600 : isDisabled ? theme.colors.inputDisabled : theme.colors.carbon400,
      padding: '7px 6px',
      '&:hover': {
        color: isDisabled ? theme.colors.inputDisabled : theme.colors.carbon600
      }
    };
  }
};
/* ======================================= */
const DropdownIndicator = props => {
  return /*#__PURE__*/React__default.createElement(components.DropdownIndicator, props, /*#__PURE__*/React__default.createElement(Icon, {
    color: props.isDisabled ? 'inputDisabled' : 'carbon600',
    name: "ArrowChevronDown",
    size: "large"
  }));
};
/* ======================================= */
/**
 * Select is essentially a combobox and abstracted from [react-select](https://react-select.com).
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/select#usage)
 * - [Props](https://blockparty.endpoint.com/components/select#props)
 * <br />
 *
 */
const Select = /*#__PURE__*/forwardRef((_ref6, forwardedRef) => {
  let {
    customStyles,
    components,
    isSearchable = false,
    isInvalid = false,
    isDisabled = false,
    id,
    ...rest
  } = _ref6;
  const formControl = useInputGroupContext();
  const _isInvalid = isInvalid || formControl.isInvalid;
  const _IsDisabled = isDisabled || formControl.isDisabled;
  const inputId = id || formControl.groupId;
  return /*#__PURE__*/React__default.createElement(ReactSelect
  // @ts-ignore
  , _extends({
    ref: forwardedRef,
    components: {
      DropdownIndicator,
      ...components
    },
    inputId: inputId,
    isDisabled: _IsDisabled // custom disabled styles
    ,
    isInvalid: _isInvalid // custom invalid styles
    ,
    isSearchable: isSearchable,
    maxMenuHeight: 300,
    menuPlacement: "auto",
    styles: {
      ...selectorStyles,
      ...customStyles
    }
  }, rest));
});

export { DropdownIndicator, Select, selectorStyles };
